
import { storageClient } from './storageClient';
import { normalizeStoragePath, validateImageUploadFile } from './upload-security';
import { resizeImage } from './utils';

export interface UploadResult {
  url: string;
  path: string;
  error?: string;
}

/* ── Server-side uploads ───────────────────────────────────────────────
   `StorageService.uploadImage` below is browser-shaped: it takes a `File`,
   validates it with `validateImageUploadFile` (File-typed), resizes it through
   a canvas, and writes with `storageClient` — which is built from the
   NEXT_PUBLIC anon key.

   Server code cannot use it. It uploads Buffers, not Files, and much of it
   writes to PRIVATE buckets (payment proofs, import files, analytics exports)
   using the service-role client. Pushing those through `storageClient` would
   silently downgrade them from service-role to anon and break RLS-protected
   writes — a security regression dressed up as consolidation.

   So the server path takes the caller's client rather than choosing one. That
   keeps every upload flowing through one audited place for path handling,
   cache headers, upsert policy and error shape, without touching which
   credentials any given call uses.

   Structural type rather than importing SupabaseClient: this module is bundled
   for the browser, and the server clients differ per call site. */
type StorageCapableClient = {
  storage: {
    from(bucket: string): {
      upload(
        path: string,
        body: ArrayBuffer | ArrayBufferView | Blob | File | string,
        options?: { cacheControl?: string; upsert?: boolean; contentType?: string },
      ): Promise<{ data: { path: string } | null; error: { message: string } | null }>;
      getPublicUrl(path: string): { data: { publicUrl: string } };
    };
  };
};

export interface UploadBufferOptions {
  contentType: string;
  cacheControl?: string;
  upsert?: boolean;
  /** Private buckets have no meaningful public URL — leave false for those so
   *  callers are not handed a link that will 400 for anyone who follows it. */
  publicUrl?: boolean;
}

/**
 * The single entry point for server-side storage uploads.
 *
 * Pass the client the call site already uses (service-role for private
 * buckets, anon for public ones) — this never picks one for you.
 */
export async function uploadBuffer(
  client: StorageCapableClient,
  bucket: string,
  path: string,
  body: ArrayBuffer | ArrayBufferView | Blob | File | string,
  options: UploadBufferOptions,
): Promise<UploadResult> {
  try {
    const normalizedPath = normalizeStoragePath(path);
    if (!normalizedPath) {
      throw new Error('Storage path is invalid.');
    }

    const { data, error } = await client.storage.from(bucket).upload(normalizedPath, body, {
      cacheControl: options.cacheControl ?? '3600',
      upsert: options.upsert ?? true,
      contentType: options.contentType,
    });

    if (error) throw new Error(error.message);

    const url = options.publicUrl
      ? client.storage.from(bucket).getPublicUrl(normalizedPath).data.publicUrl
      : '';

    return { url, path: data?.path ?? normalizedPath };
  } catch (error) {
    return {
      url: '',
      path: '',
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
}

export class StorageService {
  private static instance: StorageService;
  
  static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }

  async uploadImage(
    file: File, 
    bucket: string, 
    path: string,
    options: {
      resize?: boolean;
      maxWidth?: number;
      maxHeight?: number;
      quality?: number;
    } = {}
  ): Promise<UploadResult> {
    try {
      const validation = validateImageUploadFile(file);
      if (!validation.ok) {
        throw new Error(validation.error);
      }

      const normalizedPath = normalizeStoragePath(path);
      if (!normalizedPath) {
        throw new Error('Storage path is invalid.');
      }

      let fileToUpload = file;

      // Resize if requested and file is an image
      if (options.resize && file.type.startsWith('image/')) {
        fileToUpload = await resizeImage(file, {
          maxWidth: options.maxWidth,
          maxHeight: options.maxHeight,
          quality: options.quality,
        });
      }

      const { data, error } = await storageClient.storage
        .from(bucket)
        .upload(normalizedPath, fileToUpload, {
          cacheControl: '3600',
          upsert: true,
          contentType: fileToUpload.type,
        });

      if (error) {
        throw error;
      }

      const { data: publicUrlData } = storageClient.storage
        .from(bucket)
        .getPublicUrl(normalizedPath);

      return {
        url: publicUrlData.publicUrl,
        path: data.path,
      };
    } catch (error) {
      return {
        url: '',
        path: '',
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  async deleteFile(bucket: string, path: string): Promise<boolean> {
    try {
      const { error } = await storageClient.storage
        .from(bucket)
        .remove([path]);

      return !error;
    } catch (e) {
      console.warn('[storage] deleteFile failed:', e);
      return false;
    }
  }

  getPublicPathFromUrl(url: string, bucket: string): string | null {
    if (!url) return null;
    if (!url.startsWith('http')) {
      return url.replace(/^\/+/, '');
    }

    try {
      const parsed = new URL(url);
      const marker = `/storage/v1/object/public/${bucket}/`;
      const idx = parsed.pathname.indexOf(marker);
      if (idx === -1) return null;
      return decodeURIComponent(parsed.pathname.slice(idx + marker.length));
    } catch (e) {
      console.warn('[storage] getPublicPathFromUrl failed:', e);
      return null;
    }
  }

  getBucketAndPathFromUrl(url: string): { bucket: string; path: string } | null {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const match = parsed.pathname.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
      if (!match) return null;
      return {
        bucket: match[1],
        path: decodeURIComponent(match[2]),
      };
    } catch (e) {
      console.warn('[storage] getBucketAndPathFromUrl failed:', e);
      return null;
    }
  }

  async deleteByPublicUrl(bucket: string, url?: string | null): Promise<boolean> {
    if (!url) return false;
    const path = this.getPublicPathFromUrl(url, bucket);
    if (path) {
      return this.deleteFile(bucket, path);
    }

    const resolved = this.getBucketAndPathFromUrl(url);
    if (!resolved) return false;
    return this.deleteFile(resolved.bucket, resolved.path);
  }

  generatePath(prefix: string, filename: string): string {
    const timestamp = Date.now();
    const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).substring(2, 10);
    const normalizedName = normalizeStoragePath(filename).split('/').pop() || 'upload.jpg';
    const extension = normalizedName.split('.').pop() || 'jpg';
    return normalizeStoragePath(`${prefix}/${timestamp}-${random}.${extension}`);
  }

  async get(bucket: string, list: any) {
    return storageClient.storage.from(bucket).list(list);
  }
}

export const storage = StorageService.getInstance();
